import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ModalShell } from "../../components/ModalShell";
import {
  useAdminPlatformCredential,
  type PlatformCredentialStatus,
} from "../../hooks/useAdminPlatformCredential";
import { useAdminUpstreamOverride } from "../../hooks/useAdminUpstreamOverride";
import { IntegrationCatalogSection } from "./IntegrationCatalogSection";
import flagStyles from "./AdminFeatureFlags.module.css";
import styles from "./AdminIntegrations.module.css";
import { AdminPageIntro } from "../../components/admin/AdminPageIntro";

// IP-221: providers whose CATALOG tools we enumerate via a platform-owned
// account (an account WE authenticate, not a customer's). ServiceM8 is the only
// one today; add future native providers here.
const PLATFORM_PROVIDERS: ReadonlyArray<{ provider: string; label: string }> = [
  { provider: "servicem8", label: "ServiceM8" },
];

// Providers that support the admin hosted-mock upstream override. Must stay in
// step with the gateway's resolver wiring and ztc-core's OVERRIDABLE_PROVIDERS
// allowlist; add the next native-REST provider here, not as another inline row.
const OVERRIDABLE_PROVIDERS: ReadonlyArray<{ provider: string; label: string }> = [
  { provider: "simpro", label: "Simpro" },
];

const STATUS_LABEL: Record<PlatformCredentialStatus, { label: string; tone: string | undefined }> = {
  active: { label: "Connected", tone: styles.toneActive },
  error: { label: "Needs reconnect", tone: styles.toneError },
  absent: { label: "Not connected", tone: styles.toneAbsent },
};

const PlatformProviderRow = ({
  provider,
  label,
}: {
  provider: string;
  label: string;
}): JSX.Element => {
  const { status, loading, error, startConnect } = useAdminPlatformCredential(provider);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const titleId = `platform-connect-${provider}`;

  const onContinue = async (): Promise<void> => {
    setBusy(true);
    setConnectError(null);
    try {
      const url = await startConnect();
      window.location.assign(url);
    } catch (err) {
      setConnectError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  const copy = status ? STATUS_LABEL[status] : null;

  return (
    <div className={styles.row}>
      <div className={styles.rowMain}>
        <span className={styles.providerName}>{label}</span>
        {loading ? (
          <span className={`${styles.pill} ${styles.toneAbsent}`}>Checking…</span>
        ) : error ? (
          <span className={`${styles.pill} ${styles.toneError}`}>Status unavailable</span>
        ) : copy ? (
          <span className={`${styles.pill} ${copy.tone ?? ""}`}>{copy.label}</span>
        ) : null}
      </div>
      <button type="button" className={styles.connectBtn} onClick={() => setOpen(true)}>
        {status === "active" ? "Reconnect" : "Connect"}
      </button>

      {open ? (
        <ModalShell density="compact" titleId={titleId} onClose={() => (busy ? undefined : setOpen(false))}>
          <header className={flagStyles.dialogHeader}>
            <h2 id={titleId} className={flagStyles.dialogTitle}>
              Connect {label} (platform account)
            </h2>
            <button
              type="button"
              className={flagStyles.closeBtn}
              onClick={() => setOpen(false)}
              disabled={busy}
              aria-label="Close"
            >
              ×
            </button>
          </header>
          <p className={styles.modalBody}>
            This authorizes ZeroToClaw&apos;s own {label} account. We use it only to list{" "}
            {label}&apos;s tools in the integration catalog &mdash; it does not touch any
            customer&apos;s connection. You&apos;ll be redirected to {label} to sign in.
          </p>
          {connectError ? <p className={styles.modalError}>{connectError}</p> : null}
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Cancel
            </button>
            <button type="button" className={styles.connectBtn} onClick={onContinue} disabled={busy}>
              {busy ? "Redirecting…" : `Continue to ${label}`}
            </button>
          </div>
        </ModalShell>
      ) : null}
    </div>
  );
};

// IP-156 hosted-mock toggle: routes a native REST provider's upstream between
// the customers' real builds and our hosted mock. Global - it affects every
// connection of the provider at once - hence the confirm modal both ways.
const UpstreamOverrideRow = ({
  provider,
  label,
}: {
  provider: string;
  label: string;
}): JSX.Element => {
  const { state, loading, error, routeToMock, routeToReal } = useAdminUpstreamOverride(provider);
  const [confirming, setConfirming] = useState<"mock" | "real" | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const titleId = `upstream-override-${provider}`;
  const routed = state?.active === true;

  const onConfirm = async (): Promise<void> => {
    setBusy(true);
    setActionError(null);
    try {
      await (confirming === "mock" ? routeToMock() : routeToReal());
      setConfirming(null);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.row}>
      <div className={styles.rowMain}>
        <span className={styles.providerName}>{label}</span>
        {loading ? (
          <span className={`${styles.pill} ${styles.toneAbsent}`}>Checking…</span>
        ) : error ? (
          <span className={`${styles.pill} ${styles.toneError}`}>Status unavailable</span>
        ) : routed ? (
          <span className={`${styles.pill} ${styles.toneWarn}`}>Routed to hosted mock</span>
        ) : (
          <span className={`${styles.pill} ${styles.toneActive}`}>Real customer builds</span>
        )}
        {routed && state?.buildUrl ? <span className={styles.urlHint}>{state.buildUrl}</span> : null}
      </div>
      <button
        type="button"
        className={styles.connectBtn}
        onClick={() => setConfirming(routed ? "real" : "mock")}
        disabled={loading || !!error}
      >
        {routed ? "Route to real builds" : "Route to mock"}
      </button>

      {confirming ? (
        <ModalShell density="compact" titleId={titleId} onClose={() => (busy ? undefined : setConfirming(null))}>
          <header className={flagStyles.dialogHeader}>
            <h2 id={titleId} className={flagStyles.dialogTitle}>
              {confirming === "mock"
                ? `Route ${label} to the hosted mock?`
                : `Route ${label} back to real builds?`}
            </h2>
            <button
              type="button"
              className={flagStyles.closeBtn}
              onClick={() => setConfirming(null)}
              disabled={busy}
              aria-label="Close"
            >
              ×
            </button>
          </header>
          <p className={styles.modalBody}>
            {confirming === "mock" ? (
              <>
                This reroutes <strong>every</strong> {label} connection on the platform - tool
                calls and new connects - to our hosted mock instead of the customer&apos;s own
                build. Use it for demos and testing only; the gateway picks the change up within
                ~30 seconds.
              </>
            ) : (
              <>
                {label} connections go back to each customer&apos;s own build using their stored
                credentials. Demo connections made while the mock was active will stop working
                (their pasted credentials were never validated against a real build).
              </>
            )}
          </p>
          {actionError ? <p className={styles.modalError}>{actionError}</p> : null}
          <div className={styles.modalActions}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => setConfirming(null)}
              disabled={busy}
            >
              Cancel
            </button>
            <button type="button" className={styles.connectBtn} onClick={onConfirm} disabled={busy}>
              {busy ? "Applying…" : confirming === "mock" ? "Route to mock" : "Route to real builds"}
            </button>
          </div>
        </ModalShell>
      ) : null}
    </div>
  );
};

export const AdminIntegrations = (): JSX.Element => {
  const [params, setParams] = useSearchParams();
  const connected = params.get("platformConnected");
  const failed = params.get("platformError");

  const dismiss = (): void => {
    const next = new URLSearchParams(params);
    next.delete("platformConnected");
    next.delete("platformError");
    setParams(next, { replace: true });
  };

  return (
    <div className={styles.wrap}>
      <AdminPageIntro title="Platform authentication">
        ZeroToClaw&apos;s own provider accounts, used to list each provider&apos;s tools for the catalog. Separate from any customer&apos;s connection.
      </AdminPageIntro>

      {connected ? (
        <div className={`${styles.banner} ${styles.bannerOk}`}>
          <span>Connected the {connected} platform account.</span>
          <button type="button" className={styles.bannerDismiss} onClick={dismiss}>
            Dismiss
          </button>
        </div>
      ) : null}
      {failed ? (
        <div className={`${styles.banner} ${styles.bannerErr}`}>
          <span>Couldn&apos;t connect ({failed}).</span>
          <button type="button" className={styles.bannerDismiss} onClick={dismiss}>
            Dismiss
          </button>
        </div>
      ) : null}

      <div className={styles.list}>
        {PLATFORM_PROVIDERS.map((p) => (
          <PlatformProviderRow key={p.provider} provider={p.provider} label={p.label} />
        ))}
      </div>

      <AdminPageIntro title="Upstream routing">
        Send a native integration&apos;s API traffic to our hosted mock for demos and testing. Global per integration, so flip it back before onboarding real connections.
      </AdminPageIntro>

      <div className={styles.list}>
        {OVERRIDABLE_PROVIDERS.map((p) => (
          <UpstreamOverrideRow key={p.provider} provider={p.provider} label={p.label} />
        ))}
      </div>

      <IntegrationCatalogSection />
    </div>
  );
};
